/* MoreLuxe — global storefront behaviour
   Vanilla re-implementation of the design's React interactions:
   thin-line Lucide icons, the mobile nav drawer, and shared accordions.
   Section-specific logic (calendar, reviews carousel, collection filter)
   lives inline in those sections. */
(function () {
  'use strict';

  function drawIcons() {
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
      window.lucide.createIcons();
    }
  }

  /* ---- mobile nav drawer (drops down from beneath the sticky header) ----
     While it is open the drawer behaves as a modal: the page behind is scroll-locked
     (html.ml-drawer-open) and `inert`, Tab cycles between the burger (the close
     button) and the drawer links, and a light backdrop below the drawer swallows a
     tap outside it (the tap closes the drawer and activates nothing underneath).
     Open/close is a max-height accordion measured in JS, so the closed height is
     exactly 0 — it can never get "stuck" half-collapsed. */
  var INERT_MARK = 'data-ml-drawer-inert';
  function drawerEl() { return document.querySelector('.nav-drawer'); }
  function backdropEl() { return document.querySelector('[data-drawer-backdrop]'); }
  function drawerIsOpen() {
    var d = drawerEl();
    return !!(d && d.classList.contains('open'));
  }
  function anchorDrawer(drawer) {
    var header = document.querySelector('.hdr');
    if (drawer && header) {
      // emerge from directly under the (sticky) header bar, whatever its height
      var top = Math.round(header.getBoundingClientRect().bottom);
      drawer.style.top = top + 'px';
      var bd = backdropEl();
      if (bd) bd.style.top = top + 'px';
      return top;
    }
    return 0;
  }
  function sizeDrawer(drawer) {
    var top = anchorDrawer(drawer);
    var room = Math.max(160, window.innerHeight - top);
    drawer.style.maxHeight = Math.min(drawer.scrollHeight, room) + 'px';
  }
  function markInert(el) {
    if (!el || el.hasAttribute('inert')) return;   // never touch something already inert
    var t = el.tagName;
    if (t === 'SCRIPT' || t === 'STYLE' || t === 'LINK' || t === 'TEMPLATE' || t === 'NOSCRIPT') return;
    el.setAttribute('inert', '');
    el.setAttribute(INERT_MARK, '');
  }
  // Everything except the burger and the drawer becomes inert: every sibling along
  // the header's ancestor chain (the rest of the page, app widgets on <body>), plus
  // the other header controls.
  function lockPage(drawer) {
    var header = drawer.closest('.hdr');
    var node = header;
    while (node && node !== document.body && node.parentElement) {
      var sibs = node.parentElement.children;
      for (var i = 0; i < sibs.length; i++) { if (sibs[i] !== node) markInert(sibs[i]); }
      node = node.parentElement;
    }
    if (header) {
      header.querySelectorAll('.announce, .logo-link, .hdr-actions, .navbar').forEach(markInert);
    }
  }
  function unlockPage() {
    document.querySelectorAll('[' + INERT_MARK + ']').forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute(INERT_MARK);
    });
  }
  function trapStops(drawer) {
    var burger = document.querySelector('.hdr-burger');
    var stops = burger && burger.offsetWidth ? [burger] : [];
    drawer.querySelectorAll('a[href], button:not([disabled])').forEach(function (el) {
      if (el.offsetWidth || el.offsetHeight) stops.push(el);
    });
    return stops;
  }
  function setDrawer(open, returnFocus) {
    var drawer = drawerEl();
    var burger = document.querySelector('.hdr-burger');
    var bd = backdropEl();
    if (!drawer) return;
    var wasOpen = drawer.classList.contains('open');
    if (open) {
      sizeDrawer(drawer);                                    // sit under the pinned header, fit the viewport
    } else {
      drawer.style.maxHeight = '0px';                        // collapse fully
    }
    // Move focus back to the burger before the panel goes inert, so keyboard and
    // screen-reader users are not dropped onto <body>.
    if (!open && wasOpen && burger && (returnFocus || drawer.contains(document.activeElement))) {
      burger.focus({ preventScroll: true });   // it is pinned + visible; don't let focus() scroll a sticky
    }
    drawer.classList.toggle('open', open);
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    // Keep the collapsed drawer out of the tab order + a11y tree entirely.
    // `inert` handles focus and screen readers; the CSS visibility rule is the
    // fallback for browsers without inert support.
    if (open) { drawer.removeAttribute('inert'); } else { drawer.setAttribute('inert', ''); }
    if (open) { lockPage(drawer); } else { unlockPage(); }
    if (bd) bd.classList.toggle('is-open', open);
    document.documentElement.classList.toggle('ml-drawer-open', open);   // scroll lock
    if (burger) {
      burger.classList.toggle('is-open', open);   // morphs the two bars into an X
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    }
  }

  /* Event delegation so behaviour survives Shopify theme-editor re-renders */
  document.addEventListener('click', function (e) {
    // the burger is the single toggle (and the X that closes it)
    if (e.target.closest('.hdr-burger')) { setDrawer(!drawerIsOpen()); return; }
    // tapping a menu link closes the panel (the link still navigates)
    if (e.target.closest('.nav-drawer-list a')) { setDrawer(false); return; }
    // a tap outside the open drawer only closes it: it lands on the backdrop (or on
    // inert header chrome), so nothing underneath is activated
    if (drawerIsOpen() && !e.target.closest('.nav-drawer')) {
      e.preventDefault();
      setDrawer(false);
    }

    // NOTE: accordions are handled by each section's own scoped script
    // (e.g. the product page), to keep aria-expanded state in sync and
    // avoid a double-toggle from a second global handler.
  });

  document.addEventListener('keydown', function (e) {
    if (!drawerIsOpen()) return;
    if (e.key === 'Escape' || e.key === 'Esc') { setDrawer(false, true); return; }
    if (e.key !== 'Tab') return;
    // focus trap: cycle burger -> drawer links -> burger
    var stops = trapStops(drawerEl());
    if (!stops.length) return;
    // Every Tab step is moved by hand with preventScroll: the burger lives in the
    // sticky header, and a native focus move onto a sticky element scrolls the
    // (scroll-locked) page back to the header's static position.
    var i = stops.indexOf(document.activeElement);
    var n = stops.length;
    var next = e.shiftKey ? (i <= 0 ? n - 1 : i - 1) : (i === -1 || i === n - 1 ? 0 : i + 1);
    e.preventDefault();
    stops[next].focus({ preventScroll: true });
  });

  // keep the dropdown anchored & sized if the viewport changes while open;
  // close it if the burger layout no longer applies (e.g. resize past 980px)
  window.addEventListener('resize', function () {
    if (!drawerIsOpen()) return;
    var burger = document.querySelector('.hdr-burger');
    if (burger && !burger.offsetWidth) { setDrawer(false); return; }
    sizeDrawer(drawerEl());
  });

  // Never carry an open drawer (inert page, scroll lock) into the back/forward cache.
  window.addEventListener('pagehide', function () { if (drawerIsOpen()) setDrawer(false); });
  window.addEventListener('pageshow', function () {
    if (drawerIsOpen()) { setDrawer(false); } else { unlockPage(); document.documentElement.classList.remove('ml-drawer-open'); }
  });

  function init() { drawIcons(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify theme editor: redraw icons whenever a section is (re)loaded */
  document.addEventListener('shopify:section:load', drawIcons);
  document.addEventListener('shopify:section:select', drawIcons);
})();


/* ---- Sticky header: height contract + compact (stuck) state ----
   layout/theme.liquid defines window.mlMeasureHeader(), which writes
   --hdr-h (pinned height), --hdr-full-h and --hdr-collapse on :root. The sticky
   group uses top:-var(--hdr-collapse), so the announcement (and, on desktop, the
   logo row) scroll away natively and only one row stays pinned.
   Here we keep those numbers fresh and toggle .hdr.is-stuck (compact logo/icons in
   the pinned row + a soft shadow). The toggle has hysteresis and never changes
   layout, so scrolling back up cannot flicker.
   Public API: window.MLHeader.measure(), window.MLHeader.setCartCount(n). */
(function () {
  'use strict';
  var root = document.documentElement;
  var dims = { full: 0, collapse: 0, pinned: 0 };
  var stuck = false, ticking = false;

  function hdr() { return document.querySelector('.hdr'); }

  function measure() {
    var d = window.mlMeasureHeader ? window.mlMeasureHeader() : null;
    if (d) dims = d;
    update();
    return dims;
  }

  function update() {
    ticking = false;
    var h = hdr();
    if (!h) return;
    var y = window.pageYOffset || root.scrollTop || 0;
    var at = Math.max(1, dims.collapse);
    var next = stuck ? y > at - 12 : y >= at;   // 12px hysteresis band
    if (next !== stuck) {
      stuck = next;
      h.classList.toggle('is-stuck', stuck);
      root.classList.toggle('hdr-is-stuck', stuck);
    }
  }

  window.addEventListener('scroll', function () {
    if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
  }, { passive: true });

  var rT = null;
  function remeasureSoon() { clearTimeout(rT); rT = setTimeout(measure, 60); }
  window.addEventListener('resize', remeasureSoon);
  window.addEventListener('load', measure);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  document.addEventListener('shopify:section:load', function () { stuck = false; measure(); });

  function observe() {
    var h = hdr();
    if (h && 'ResizeObserver' in window) {
      new ResizeObserver(remeasureSoon).observe(h);
    }
    measure();
  }

  /* ---- cart count: badge hidden at 0, link label kept in sync ----
     Any script may call MLHeader.setCartCount(n) — or simply rewrite the text of
     `.hdr .badge`; the observer below re-syncs hidden + aria-label either way. */
  function label(n) { return 'Bag, ' + n + (n === 1 ? ' item' : ' items'); }
  function syncBadges(n) {
    var badges = document.querySelectorAll('.hdr .badge');
    for (var i = 0; i < badges.length; i++) {
      var b = badges[i];
      var v = (n === undefined) ? parseInt(b.textContent, 10) || 0 : n;
      if (n !== undefined && b.textContent !== String(v)) b.textContent = String(v);
      if (v > 0) b.removeAttribute('hidden'); else b.setAttribute('hidden', '');
    }
    var links = document.querySelectorAll('[data-cart-link]');
    for (var j = 0; j < links.length; j++) {
      var m = (n === undefined) ? (parseInt((links[j].querySelector('.badge') || {}).textContent, 10) || 0) : n;
      links[j].setAttribute('data-cart-count', String(m));
      links[j].setAttribute('aria-label', label(m));
    }
  }
  function setCartCount(n) { syncBadges(Math.max(0, parseInt(n, 10) || 0)); }
  function watchBadges() {
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function () { syncBadges(); });
    var badges = document.querySelectorAll('.hdr .badge');
    for (var i = 0; i < badges.length; i++) mo.observe(badges[i], { childList: true, characterData: true, subtree: true });
  }

  window.MLHeader = { measure: measure, setCartCount: setCartCount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(); watchBadges(); });
  } else {
    observe(); watchBadges();
  }
})();


/* ---- Designers dropdown ----
   Mouse: opens on hover (CSS, @media (hover:hover)) — the JS hover sync below
   reacts to pointerType 'mouse' only, so a touch tap is never mistaken for hover.
   Touch/pen: the first tap opens the menu instead of navigating; a second tap
   follows the link. Keyboard: opens on focus; Escape closes it (even with focus
   inside, via .is-closed) and returns focus to the trigger; ArrowDown reopens it.
   Any outside click closes it. Delegated, so it survives theme-editor reloads. */
(function () {
  var lastPointer = 'mouse';
  function itemOf(el) { return el && el.closest ? el.closest('.nav-item--dd') : null; }
  function trigOf(item) { return item.querySelector('.nav-dd-trigger'); }
  function setExpanded(item, open) {
    item.classList.toggle('is-open', open);
    if (open) item.classList.remove('is-closed');
    var t = trigOf(item);
    if (t) t.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  function closeAll(except) {
    var open = document.querySelectorAll('.nav-item--dd.is-open');
    for (var i = 0; i < open.length; i++) if (open[i] !== except) setExpanded(open[i], false);
  }

  document.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || 'mouse'; }, true);

  document.addEventListener('click', function (e) {
    var trig = e.target.closest ? e.target.closest('.nav-dd-trigger') : null;
    // e.detail === 0 → keyboard-activated click (Enter): always follow the link
    var touchy = e.detail !== 0 && (lastPointer === 'touch' || lastPointer === 'pen' ||
      window.matchMedia('(hover: none)').matches);
    if (trig && touchy) {
      var item = itemOf(trig);
      if (item && !item.classList.contains('is-open')) {
        e.preventDefault();
        closeAll(item);
        setExpanded(item, true);
        return;
      }
    }
    if (!itemOf(e.target)) closeAll(null);
  });

  document.addEventListener('keydown', function (e) {
    var items = document.querySelectorAll('.nav-item--dd');
    if (e.key === 'Escape' || e.key === 'Esc') {
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var focusIn = it.contains(document.activeElement);
        if (it.classList.contains('is-open') || focusIn || it.matches(':hover')) {
          setExpanded(it, false);
          it.classList.add('is-closed');
          if (focusIn) { var t = trigOf(it); if (t) t.focus({ preventScroll: true }); }
        }
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      var tr = e.target.closest ? e.target.closest('.nav-dd-trigger') : null;
      var item = itemOf(tr);
      if (!item) return;
      e.preventDefault();
      setExpanded(item, true);
      var first = item.querySelector('.nav-dd-link');
      if (first) first.focus();
    }
  });

  // hover sync for aria-expanded — mouse only
  document.addEventListener('pointerover', function (e) {
    if (e.pointerType !== 'mouse') return;
    var it = itemOf(e.target);
    if (it && !(e.relatedTarget && it.contains(e.relatedTarget))) { it.classList.remove('is-closed'); setExpanded(it, true); }
  });
  document.addEventListener('pointerout', function (e) {
    if (e.pointerType !== 'mouse') return;
    var it = itemOf(e.target);
    if (it && !(e.relatedTarget && it.contains(e.relatedTarget))) {
      it.classList.remove('is-closed');
      if (!it.contains(document.activeElement)) setExpanded(it, false);
    }
  });
  document.addEventListener('focusin', function (e) {
    var it = itemOf(e.target);
    // keyboard focus only: a tap also focuses the trigger (before its click), and
    // opening here would make that first tap navigate instead of opening the menu
    var kb = true;
    try { kb = e.target.matches(':focus-visible'); } catch (err) {}
    if (it && kb && !it.classList.contains('is-closed')) setExpanded(it, true);
  });
  document.addEventListener('focusout', function (e) {
    var it = itemOf(e.target);
    if (it && !(e.relatedTarget && it.contains(e.relatedTarget))) {
      it.classList.remove('is-closed');
      if (!it.matches(':hover')) setExpanded(it, false);
    }
  });
})();
